import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import AuthVerify from "@/pages/AuthVerify";
import MemberDashboard from "@/pages/member/Dashboard";
import MemberProfile from "@/pages/member/Profile";
import MemberData from "@/pages/member/DataRgpd";
import Contact from "@/pages/member/Contact";
import Actualites from "@/pages/member/Actualites";
import ActualiteDetail from "@/pages/member/ActualiteDetail";
import MemberDocuments from "@/pages/member/Documents";
import MemberDocumentDetail from "@/pages/member/DocumentDetail";
import MemberSeminars from "@/pages/member/Seminars";
import MemberSeminarDetail from "@/pages/member/SeminarDetail";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import MembersList from "@/pages/admin/MembersList";
import MemberForm from "@/pages/admin/MemberForm";
import MemberDetail from "@/pages/admin/MemberDetail";
import MemberImport from "@/pages/admin/MemberImport";
import AdminUsers from "@/pages/admin/AdminUsers";
import AuditLog from "@/pages/admin/AuditLog";
import DocumentsAdmin from "@/pages/admin/DocumentsAdmin";
import CategoriesAdmin from "@/pages/admin/CategoriesAdmin";
import MessagerieCampagnes from "@/pages/admin/MessagerieCampagnes";
import MessagerieParametres from "@/pages/admin/MessagerieParametres";
import HelloAssoAdmin from "@/pages/admin/HelloAssoAdmin";
import SeminarsList from "@/pages/admin/SeminarsList";
import SeminarForm from "@/pages/admin/SeminarForm";
import SeminarEnrollments from "@/pages/admin/SeminarEnrollments";
import ActualitesAdmin from "@/pages/admin/ActualitesAdmin";
import ArchitectureAdmin from "@/pages/admin/ArchitectureAdmin";
import AnnuaireAdmin from "@/pages/admin/AnnuaireAdmin";
import ModulesAdmin from "@/pages/admin/ModulesAdmin";
import DoublonsAdmin from "@/pages/admin/DoublonsAdmin";
import ReferentielsAdmin from "@/pages/admin/ReferentielsAdmin";
import OnboardingAdmin from "@/pages/admin/OnboardingAdmin";
import Annuaire from "@/pages/member/Annuaire";

function FullScreenLoader() {
  return (
    <div className="min-h-screen bg-cfhtb-bg flex items-center justify-center">
      <div className="h-8 w-8 border-2 border-cfhtb-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/connexion" state={{ from: location }} replace />;
  return children;
}

function RequireAdmin({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/connexion" replace />;
  if (!user.is_admin) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/connexion" element={<Login step="email" />} />
            <Route path="/connexion/verification" element={<Login step="code" />} />
            <Route path="/auth/:token" element={<AuthVerify />} />

            <Route path="/" element={<RequireAuth><MemberDashboard /></RequireAuth>} />
            <Route path="/actualites" element={<RequireAuth><Actualites /></RequireAuth>} />
            <Route path="/actualites/:slug" element={<RequireAuth><ActualiteDetail /></RequireAuth>} />
            <Route path="/documents" element={<RequireAuth><MemberDocuments /></RequireAuth>} />
            <Route path="/documents/:id" element={<RequireAuth><MemberDocumentDetail /></RequireAuth>} />
            <Route path="/seminaires" element={<RequireAuth><MemberSeminars /></RequireAuth>} />
            <Route path="/seminaires/:slug" element={<RequireAuth><MemberSeminarDetail /></RequireAuth>} />
            <Route path="/annuaire" element={<RequireAuth><Annuaire /></RequireAuth>} />
            <Route path="/profil" element={<RequireAuth><MemberProfile /></RequireAuth>} />
            <Route path="/profil/donnees" element={<RequireAuth><MemberData /></RequireAuth>} />
            <Route path="/contact" element={<RequireAuth><Contact /></RequireAuth>} />

            <Route path="/admin" element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />
            <Route path="/admin/membres" element={<RequireAdmin><MembersList /></RequireAdmin>} />
            <Route path="/admin/membres/nouveau" element={<RequireAdmin><MemberForm /></RequireAdmin>} />
            <Route path="/admin/membres/import" element={<RequireAdmin><MemberImport /></RequireAdmin>} />
            <Route path="/admin/membres/:id/modifier" element={<RequireAdmin><MemberForm edit /></RequireAdmin>} />
            <Route path="/admin/membres/:id" element={<RequireAdmin><MemberDetail /></RequireAdmin>} />
            <Route path="/admin/documents" element={<RequireAdmin><DocumentsAdmin /></RequireAdmin>} />
            <Route path="/admin/documents/categories" element={<RequireAdmin><CategoriesAdmin /></RequireAdmin>} />
            <Route path="/admin/actualites" element={<RequireAdmin><ActualitesAdmin /></RequireAdmin>} />
            <Route path="/admin/annuaire" element={<RequireAdmin><AnnuaireAdmin /></RequireAdmin>} />
            <Route path="/admin/doublons" element={<RequireAdmin><DoublonsAdmin /></RequireAdmin>} />
            <Route path="/admin/referentiels" element={<RequireAdmin><ReferentielsAdmin /></RequireAdmin>} />
            <Route path="/admin/modules" element={<RequireAdmin><ModulesAdmin /></RequireAdmin>} />
            <Route path="/admin/messagerie/onboarding" element={<RequireAdmin><OnboardingAdmin /></RequireAdmin>} />
            <Route path="/admin/architecture" element={<RequireAdmin><ArchitectureAdmin /></RequireAdmin>} />
            <Route path="/admin/seminaires" element={<RequireAdmin><SeminarsList /></RequireAdmin>} />
            <Route path="/admin/seminaires/nouveau" element={<RequireAdmin><SeminarForm /></RequireAdmin>} />
            <Route path="/admin/seminaires/:id/modifier" element={<RequireAdmin><SeminarForm edit /></RequireAdmin>} />
            <Route path="/admin/seminaires/:id/inscrits" element={<RequireAdmin><SeminarEnrollments /></RequireAdmin>} />
            <Route path="/admin/utilisateurs" element={<RequireAdmin><AdminUsers /></RequireAdmin>} />
            <Route path="/admin/journal" element={<RequireAdmin><AuditLog /></RequireAdmin>} />
            <Route path="/admin/messagerie" element={<RequireAdmin><MessagerieCampagnes /></RequireAdmin>} />
            <Route path="/admin/messagerie/parametres" element={<RequireAdmin><MessagerieParametres /></RequireAdmin>} />
            <Route path="/admin/helloasso" element={<RequireAdmin><HelloAssoAdmin /></RequireAdmin>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
